import { ApolloCache, useMutation, useQuery } from "@apollo/client";
import {
  REVOKE_TASK_CALENDAR_TOKEN_MUTATION,
  SAVE_TASK_CALENDAR_SUBSCRIPTION_MUTATION,
  TASK_CALENDAR_SUBSCRIPTION_QUERY,
} from "components/Tasks/schema";
import {
  SaveTaskCalendarSubscriptionMutationVariables,
  TaskCalendarSubscriptionQuery,
} from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useCallback } from "react";

export type CalendarSubscription = NonNullable<
  TaskCalendarSubscriptionQuery["taskCalendarSubscription"]
>;

type SaveInput = Omit<SaveTaskCalendarSubscriptionMutationVariables, "tenantId">;

/** Keeps the subscription query aligned with the latest save or revoke result. */
function writeTaskCalendarSubscription<T>(
  cache: ApolloCache<T>,
  tenantId: string,
  subscription: CalendarSubscription | null
) {
  cache.writeQuery({
    query: TASK_CALENDAR_SUBSCRIPTION_QUERY,
    variables: { tenantId },
    data: { taskCalendarSubscription: subscription },
  });
}

/**
 * Loads and changes the signed-in user's tenant-wide calendar subscription:
 * its URL, which tasks it includes, and the saved filter for a custom feed.
 */
export function useCalendarSubscription() {
  const { tenantId } = useTenant();
  const resolvedTenantId = tenantId ?? "";

  const { data, loading } = useQuery(TASK_CALENDAR_SUBSCRIPTION_QUERY, {
    variables: { tenantId: resolvedTenantId },
    skip: !tenantId,
  });

  const [saveSubscription, { loading: saving }] = useMutation(
    SAVE_TASK_CALENDAR_SUBSCRIPTION_MUTATION
  );
  const [revokeToken, { loading: revoking }] = useMutation(REVOKE_TASK_CALENDAR_TOKEN_MUTATION);

  const subscription = data?.taskCalendarSubscription ?? null;

  /** Creates the subscription if there isn't one; otherwise updates it in place. */
  const save = useCallback(
    async (input: SaveInput) => {
      if (!tenantId) {
        throw new Error("Organization is required");
      }

      await saveSubscription({
        variables: { tenantId, ...input },
        update: (cache, { data: mutationData }) => {
          const saved = mutationData?.saveTaskCalendarSubscription;
          if (!saved) {
            return;
          }
          writeTaskCalendarSubscription(cache, tenantId, saved);
        },
      });
    },
    [saveSubscription, tenantId]
  );

  const revokeUrl = useCallback(async () => {
    if (!tenantId) {
      throw new Error("Organization is required");
    }
    const result = await revokeToken({
      variables: { tenantId },
      optimisticResponse: { revokeTaskCalendarToken: true },
      update: (cache, { data: mutationData }) => {
        if (!mutationData?.revokeTaskCalendarToken) {
          return;
        }
        writeTaskCalendarSubscription(cache, tenantId, null);
      },
    });
    return Boolean(result.data?.revokeTaskCalendarToken);
  }, [revokeToken, tenantId]);

  return { subscription, loading, saving, revoking, save, revokeUrl };
}
